import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-6xl md:text-7xl font-serif font-bold text-[#3D2817] mb-6">
            About Us
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light">
            More than just a café - we're part of the Otley community
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#3D2817] mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  The Bridge Street Café opened its doors in 2018 with a simple mission: to create a warm, welcoming space where the Otley community could gather, connect, and enjoy great food and coffee.
                </p>
                <p>
                  What started as a dream has grown into a cherished local spot, where regulars are greeted by name and newcomers quickly become part of our extended family. We believe that a café should be more than just a place to grab a quick coffee - it should be a home away from home.
                </p>
                <p>
                  Every day, we're proud to serve our community with genuine hospitality, quality ingredients, and the kind of care that can only come from a truly independent, locally-owned business.
                </p>
              </div>
            </div>
            
            <div className="rounded-3xl overflow-hidden shadow-2xl" style={{ height: '450px' }}>
              <img 
                src="https://public.readdy.ai/ai/img_res/c24ebacdd3ed0cc9c7f32f767a03dfc2.jpg" 
                alt="Our Team" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Community Focus Section */}
      <section className="py-16 px-6 bg-[#F5F3F0]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 rounded-3xl overflow-hidden shadow-2xl" style={{ height: '450px' }}>
              <img 
                src="https://readdy.ai/api/search-image?query=Community%20gathering%20in%20cafe%20local%20event%20people%20socializing%20neighbors%20meeting%20friendly%20atmosphere%20British%20cafe%20community%20spirit%20Yorkshire%20town%20social%20hub%20welcoming%20inclusive%20environment%20diverse%20customers&width=600&height=450&seq=cafe-about-community-010&orientation=portrait" 
                alt="Community Gathering" 
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="order-1 md:order-2">
              <h2 className="text-4xl font-bold text-[#3D2817] mb-6">Community at Heart</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Being part of the Otley community isn't just something we say - it's who we are. We host local artists' work on our walls, support community events, and provide a space where everyone feels welcome.
                </p>
                <p>
                  From new parents meeting for morning coffee to students studying in the afternoons, to retirees enjoying a catch-up with friends - our café is a place for all generations to come together.
                </p>
                <p>
                  We're passionate about supporting other local businesses too. Whether it's displaying flyers for community events or recommending other independent shops in town, we believe in the power of community over competition.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Locally Sourced Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#3D2817] mb-6">Locally Sourced Ingredients</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
                <p>
                  Quality starts with great ingredients, which is why we're committed to sourcing locally wherever possible. Our relationships with Yorkshire suppliers ensure that what you eat and drink is fresh, seasonal, and supports our regional economy.
                </p>
                <p>
                  From our freshly baked bread delivered daily to the free-range eggs in our breakfast dishes, we carefully select suppliers who share our values of quality, sustainability, and community.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#C17855] rounded-full flex-shrink-0">
                    <i className="ri-cup-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#3D2817] mb-1">Coffee Beans</h3>
                    <p className="text-sm text-gray-600">Roasted locally in Yorkshire by artisan roasters</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#C17855] rounded-full flex-shrink-0">
                    <i className="ri-cake-3-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#3D2817] mb-1">Fresh Baking</h3>
                    <p className="text-sm text-gray-600">Bread and pastries from local bakeries</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#C17855] rounded-full flex-shrink-0">
                    <i className="ri-restaurant-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#3D2817] mb-1">Seasonal Produce</h3>
                    <p className="text-sm text-gray-600">Fresh vegetables and ingredients from regional farms</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#C17855] rounded-full flex-shrink-0">
                    <i className="ri-leaf-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#3D2817] mb-1">Dairy Products</h3>
                    <p className="text-sm text-gray-600">Fresh milk and cheese from Yorkshire dairies</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="rounded-3xl overflow-hidden shadow-2xl" style={{ height: '550px' }}>
              <img 
                src="https://readdy.ai/api/search-image?query=Fresh%20local%20ingredients%20on%20wooden%20table%20Yorkshire%20produce%20vegetables%20eggs%20dairy%20products%20seasonal%20farm%20fresh%20artisan%20quality%20simple%20clean%20background%20locally%20sourced%20natural%20organic%20ingredients%20British%20regional%20food&width=600&height=550&seq=cafe-about-ingredients-011&orientation=portrait" 
                alt="Locally Sourced Ingredients" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Friendly Atmosphere Section */}
      <section className="py-16 px-6 bg-[#F5F3F0]">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-[#3D2817] mb-6">A Friendly Atmosphere</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed">
            Walk through our doors and you'll immediately feel the difference. Whether you're popping in for a quick takeaway or settling in for a leisurely brunch, our team is here to make you feel at home. It's not just about the food and drink - it's about creating moments of connection and comfort in your day.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="w-16 h-16 mx-auto flex items-center justify-center bg-[#C4D4C0] rounded-full mb-6">
                <i className="ri-user-smile-line text-[#3D2817] text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#3D2817] mb-3">Friendly Service</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Our team genuinely cares about making your visit special every single time
              </p>
            </div>
            
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="w-16 h-16 mx-auto flex items-center justify-center bg-[#F4D4C8] rounded-full mb-6">
                <i className="ri-home-heart-line text-[#3D2817] text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#3D2817] mb-3">Cosy Space</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Comfortable seating and warm décor create the perfect environment to relax
              </p>
            </div>
            
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="w-16 h-16 mx-auto flex items-center justify-center bg-[#C17855]/20 rounded-full mb-6">
                <i className="ri-chat-smile-3-line text-[#3D2817] text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#3D2817] mb-3">Great Conversations</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                A place where strangers become regulars and regulars become friends
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl font-bold text-[#3D2817] mb-6">
            Come Visit Us
          </h2>
          <p className="text-xl text-gray-600 mb-10 font-light">
            Experience the warmth and quality that makes us Otley's favourite café
          </p>
          <a 
            href="/contact" 
            className="inline-flex items-center gap-3 px-10 py-5 bg-[#C17855] text-white rounded-full font-medium hover:bg-[#A66545] transition-colors whitespace-nowrap cursor-pointer shadow-xl text-lg"
          >
            Find Us
            <i className="ri-map-pin-line text-xl"></i>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}